<template>
  <!-- <div class="step-wrapper"> </div> -->
  <div class="step" :class="[status, { isActive, isComplete, isLastItem }]">
    <div>
      <div class="circle">{{ stepContent }}</div>
    </div>
    <div class="step-content">
      <div class="circle-tail status-item-tail"></div>
      <div
        class="title"
        @click="handleTitleClick"
        :class="[{ clickable: isClickable, isActive }, status]"
      >
        <slot name="title"
          >{{ title }} {{ showCompletedIndicatorText && isComplete ? " --> Completed!" : "" }}</slot
        >
      </div>
      <div class="alert"><slot name="alert"></slot></div>
      <div class="caption"><slot name="caption"></slot></div>
      <slot></slot>
    </div>
  </div>
</template>
<script setup lang="ts">
import { defineProps, computed, defineEmits, PropType, ref } from "vue";
import { TimelineMilestone, TimelineMilestoneStatus } from "@/models/applicationStatus";

const emit = defineEmits(["titleClicked"]);

const props = defineProps({
  status: {
    type: String,
    default: "",
  },
  step: {
    type: Number,
    default: 1,
  },
  title: {
    type: String,
    default: "",
  },
  configuration: {
    type: Object as PropType<TimelineMilestone>,
  },
  maximumStep: {
    type: Number,
    default: 1,
  },
});

const showCompletedIndicatorText = ref<boolean>(false);
const complete = computed<boolean>(() => props.status === "complete");
const isActive = computed<boolean>(() => props.configuration?.isActive || false);
const isComplete = computed<boolean>(
  () => props.configuration?.status === TimelineMilestoneStatus.COMPLETE,
);
const isLastItem = computed<boolean>(() => props.step >= props.maximumStep);
const isClickable = computed<boolean>(
  () => (props.configuration?.title && (isActive.value || isComplete.value)) || false,
);

const stepContent = computed(() => {
  if (props.status === "declined") {
    return "!";
  }

  return complete.value ? "✓" : props.step;
});

function handleTitleClick() {
  // only emit if there is a title to click on and status is not inactive
  if (props.configuration?.title && (isActive.value || isComplete.value)) {
    emit("titleClicked", props.configuration.step);
  }
}
</script>
<style scoped lang="scss">
.step {
  position: relative;
  min-height: 1em;
  color: gray;

  .inline-link {
    text-decoration: underline;
    cursor: pointer;
  }

  .title {
    line-height: 1.5em;
    font-weight: 700;

    &.clickable {
      cursor: pointer;
    }
    &.alert {
      color: red;
    }
  }

  .caption {
    font-size: 1em;
    font-weight: normal;

    a {
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .alert {
    color: red;
    padding: 0;
    margin: 0;
    //font-size: 0.8em;
  }

  .circle {
    background: gray;
    position: relative;
    width: 1.5em;
    height: 1.5em;
    line-height: 1.5em;
    border-radius: 100%;
    color: #fff;
    text-align: center;
    box-shadow: 0 0 0 3px #fff;
    // z-index: 999;

    // &:after {
    //   content: " ";
    //   position: absolute;
    //   display: block;
    //   top: 1px;
    //   right: 50%;
    //   bottom: 1px;
    //   left: 50%;
    //   height: 100%;
    //   width: 1px;
    //   transform: scale(1, 2);
    //   transform-origin: 50% -100%;
    //   background-color: rgba(0, 0, 0, 0.25);
    //   overflow: hidden;
    // }
  }

  &.cancelled {
    color: #000;

    .circle {
      background-color: #ffe699;
    }
  }

  &.declined {
    color: #000;

    .circle {
      background-color: #bb0000;
    }
  }

  &.pending {
    color: #000;

    .circle {
      background-color: #ffcc00;

      &:after {
        height: 120%;
      }
    }
  }

  &.active {
    color: #000;

    .circle {
      background-color: var(--theme-color);

      // &:after {
      //   height: 120%;
      // }
    }
  }

  &.complete {
    .title {
      color: green;
    }
    .circle {
      /* background-color: var(--theme-color); */
      background-color: green;
    }
  }
}

.step:is(.isLastItem) {
  .circle {
    &:after {
      display: none;
      height: 0;
    }
  }
}
.step + .step {
  margin-top: 1.5em;
}

.step > div:first-child {
  position: static;
  height: 0;
}

.step > div:not(:first-child) {
  margin-left: 1.5em;
  padding-left: 1em;
}

.step:last-child .circle:after {
  display: none;
}
/* hide the status item tail if last step in timeline */
.step:is(.isLastItem) .status-item-tail {
  display: none;
}
.status-item-tail {
  position: absolute;
  top: 1.25em;
  left: 0.75em;
  width: 2px;
  height: calc(100% - 1.25em);
  background-color: gray;
  height: 100%;
}

@media (min-width: 992px) {
  .step .circle {
    z-index: 1;
  }
}
</style>
